import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * RepoAuthorizeModal — 代码仓库授权弹窗（P0 工蜂 Git 已完整实现；
 * P1 私有化 SVN 见 2026-08-13 新增，对应 spec §2.7/§7.1；
 * 工蜂 TSVN 见 2026-09-08 新增，TAPD 1020421727137935765）
 *
 * 交互流程（与 spec `20260803_crashclaw工作区代码仓库授权(工蜂Git_SSH_SVN_P4)_spec.md`
 * §7.1/§2.3.7/§2.7 对齐，每种类型限单组认证，本组件对用户不暴露"认证组"概念）：
 *   0. 【2026-08-13 新增】打开时先展示"类型选择"卡片：工蜂 Git / SVN / 工蜂 TSVN
 *      （2026-09-08 新增第三张卡片，P4 等 P2 开工时再加）
 *   Git / 工蜂 TSVN 流程完全对称（选中对应卡片后，`repoType` 状态区分 'git'/'tsvn'）：
 *   1. 调 listGiteeRepos 查询可访问仓库（`type=GIT` 或 `type=SVN`）：
 *      - 未完成工蜂授权（authorized=false）→ 展示「连接工蜂账号」按钮，点击直接触发
 *        工蜂原生 OAuth2（PKCE）授权流程（见 §2.3.7），不再引导跳转「连接器」Tab
 *        ——那是给太湖 OAuth/MCP 能力用的，跟这里彻底解耦；保留一个次要的"手动配置
 *        Private Token"入口作为兜底。**该授权是工蜂账号级的，Git/TSVN 共用，任一
 *        卡片连接过账号后另一张卡片不需要再连一次**。
 *      - 已授权 → 多选仓库列表（支持搜索），每项可展开选择"分支/子路径"
 *        （Git 懒加载 listGitBranches；TSVN 懒加载 listTsvnPaths，SVN 无分支概念，
 *        用 trunk/branches/tags 目录树代替）
 *   2. 点击「下一步」→ 调 createGitCredential / createTsvnCredential（幂等，首次才真正
 *      等 Bridge 生成密钥对；两者各自独立生成一把密钥，互不复用，见 design.md 风险点 2）
 *      → 展示返回的公钥 + 复制按钮 + 引导文案
 *   3. 点击「保存」→ 调 createRepoBindings 批量提交选中的仓库 + 分支/路径
 *
 *   SVN 流程（选中 SVN 卡片后，2026-08-13 新增，仅服务私有化部署 SVN，即
 *   `repoType=SVN_SELF_HOSTED`，不服务工蜂原生 SVN——那是上面的 TSVN 卡片，见 spec §2.7）：
 *   1. 填写 仓库 URL / 用户名 / 密码 → 点击「校验连接」→ 调 testSvnConnection
 *      （经 Bridge 转发，不落库，纯探测；Bridge 离线/认证失败/URL 非法分别展示不同文案）
 *   2. 校验通过后展示可选的子路径列表（多选，可以什么都不选代表只授权根路径）
 *   3. 点击「保存」→ 调 createSvnCredential（内部会再校验一次连接才落库，见 §5.2.6）
 *      → 再调 createRepoBindings 批量提交选中的路径
 *
 * 后端接口：
 *   - POST /redir/api/crashclaw/listGiteeRepos（type=GIT|SVN，2026-09-08 新增 type 参数）
 *   - POST /redir/api/crashclaw/listGitBranches
 *   - POST /redir/api/crashclaw/listTsvnPaths（2026-09-08 新增，选 trunk/branches/tags）
 *   - POST /redir/api/crashclaw/createGitCredential
 *   - POST /redir/api/crashclaw/createTsvnCredential（2026-09-08 新增）
 *   - POST /redir/api/crashclaw/createRepoBindings（Git/SVN/TSVN 共用）
 *   - POST /redir/api/crashclaw/gongfengOauthAuthorize（发起工蜂原生 OAuth2 授权，见 §2.3.7）
 *   - POST /redir/api/crashclaw/configureGiteePrivateToken（次要兜底）
 *   - POST /redir/api/crashclaw/testSvnConnection（2026-08-13 新增，见 §5.2.4）
 *   - POST /redir/api/crashclaw/createSvnCredential（2026-08-13 新增，见 §5.2.6）
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Empty, Input, message, Modal, Select, Spin, Steps, Tooltip } from 'antd';
import { CloudServerOutlined, CopyOutlined, DatabaseOutlined, FolderOutlined, GithubOutlined, ReloadOutlined, SearchOutlined, } from '@ant-design/icons';
import { ze } from 'utils/zhEn';
import RestHelper from 'utils/RestHelper';
import { CRASHCLAW_NESTED_MODAL_Z_INDEX } from './crashClawDefaults';
import CsFakePasswordInput from 'components/antd-extension/CsFakePasswordInput';
import style from './style.scss';
const API_PREFIX = '/redir/api/crashclaw';
/** SVN 连接校验失败的 errorReason → 展示文案（见 spec §5.2.4）。 */
const SVN_ERROR_REASON_TEXT = {
    AUTH_FAILED: { zh: 'SVN 认证失败，请检查用户名/密码是否正确', en: 'SVN authentication failed, please check username/password' },
    INVALID_URL: { zh: 'SVN 仓库 URL 格式非法或不存在', en: 'The SVN repository URL is invalid or does not exist' },
    NETWORK_UNREACHABLE: { zh: '无法连接到 SVN 服务器，请检查网络或稍后重试', en: 'Unable to reach the SVN server, please check the network or retry later' },
    BRIDGE_OFFLINE: { zh: '该工作区 Bridge 未连接，请确保 Bridge 程序正在运行后重试', en: "This workspace's Bridge is offline, please bring it online and retry" },
    // 2026-08-13 review 修复：这个原因跟网络/鉴权都无关，是 Bridge 所在机器压根没装
    // svn 命令行，之前误归类成 NETWORK_UNREACHABLE 会让用户去查网络、查不出结果，
    // 这里给出明确到位、可操作的安装指引（两条命令都给，不假设用户容器的发行版）。
    SVN_CLI_NOT_INSTALLED: {
        zh: '该工作区 Bridge 尚未安装 svn 命令行客户端，请登录容器执行 apt-get install -y subversion（Debian/Ubuntu 系）或 yum install -y subversion（CentOS/tlinux 系）后重试',
        en: 'The svn CLI is not installed on this workspace\'s Bridge host. Please run `apt-get install -y subversion` (Debian/Ubuntu) or `yum install -y subversion` (CentOS/tlinux) inside the container, then retry',
    },
    // 2026-08-13 晚测试验证修复：旧 Bridge 不认识 repo_svn_test，原先会空等 25s 后
    // 误报 NETWORK_UNREACHABLE。缺 bridgeCapabilities.repo_svn_test 时走这条。
    BRIDGE_OUTDATED: {
        zh: '该工作区的 Bridge 版本过低，不支持 SVN 授权。请先在工作区详情页升级 Bridge 后再试',
        en: "This workspace's Bridge is too old to support SVN authorization. Please upgrade Bridge from the workspace detail page, then retry",
    },
};
function describeSvnErrorReason(reason, fallbackMessage) {
    const entry = reason ? SVN_ERROR_REASON_TEXT[reason] : undefined;
    if (entry)
        return ze(entry.zh, entry.en);
    return fallbackMessage || ze('连接校验失败，请稍后重试', 'Connection check failed, please retry later');
}
/**
 * 2026-08-04 晚新增（见 spec §2.3.6 / §10"P0 补丁"）：listGiteeRepos/listGitBranches
 * 改为经 Bridge 转发后，新增了一类"这个工作区 Bridge 离线"的错误场景，跟"工蜂 API
 * 调用失败"（鉴权/网络问题）应该展示不同文案，避免用户误以为是自己权限配置有问题。
 * 后端异常 message 里带有"Bridge"关键字时（对应 illegalArgument("...Bridge 未连接...")
 * 或 internalError("该工作区无法访问工蜂服务...")）走离线文案，其余走通用兜底文案。
 */
function describeGongfengApiError(e, fallback) {
    const backendMessage = e?.message;
    if (backendMessage && backendMessage.includes('Bridge')) {
        return ze(`该工作区的 Bridge 未连接或无法访问工蜂服务：${backendMessage}`, `This workspace's Bridge is offline or cannot reach Gitee: ${backendMessage}`);
    }
    return fallback;
}
const RepoAuthorizeModal = ({ visible, workspaceUid, bridgeOnline = true, boundRepoIdentifiers, svnCapabilityMissing = false, svnCliKnownMissing = false, existingSvnCredential = null, onHostCapabilitiesRefreshed, onClose, onSaved, }) => {
    const [phase, setPhase] = useState('type-select');
    const [repoType, setRepoType] = useState(null);
    // 2026-08-14：已有 SVN 凭证时进入"追加路径"模式（复用已存凭证，不重填 url/username/password）
    const svnAddPathsMode = !!existingSvnCredential;
    // ---- Git 流程状态（P0，不变） ----
    const [repos, setRepos] = useState([]);
    const [keyword, setKeyword] = useState('');
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState({});
    const [branchByRepo, setBranchByRepo] = useState({});
    const [branchOptionsByRepo, setBranchOptionsByRepo] = useState({});
    const [branchLoadingRepo, setBranchLoadingRepo] = useState(null);
    const [checkingOAuth, setCheckingOAuth] = useState(false);
    const [connectingOAuth, setConnectingOAuth] = useState(false);
    const [showPrivateTokenInput, setShowPrivateTokenInput] = useState(false);
    const [privateTokenValue, setPrivateTokenValue] = useState('');
    const [savingPrivateToken, setSavingPrivateToken] = useState(false);
    const [loadReposErrorMessage, setLoadReposErrorMessage] = useState('');
    const [credentialUid, setCredentialUid] = useState('');
    const [sshPublicKey, setSshPublicKey] = useState('');
    const [creatingCredential, setCreatingCredential] = useState(false);
    const [saving, setSaving] = useState(false);
    // ---- SVN 流程状态（2026-08-13 新增 P1，仅服务 SVN_SELF_HOSTED，见 spec §2.7） ----
    const [svnUrl, setSvnUrl] = useState('');
    const [svnUsername, setSvnUsername] = useState('');
    const [svnPassword, setSvnPassword] = useState('');
    const [testingSvn, setTestingSvn] = useState(false);
    const [svnTestErrorText, setSvnTestErrorText] = useState('');
    const [svnCandidatePaths, setSvnCandidatePaths] = useState([]);
    const [svnSelectedPaths, setSvnSelectedPaths] = useState({});
    const [savingSvn, setSavingSvn] = useState(false);
    // ---- 2026-08-14 新增：SVN 环境未就绪步骤的「重新检测」相关状态 ----
    const [refreshingHostCaps, setRefreshingHostCaps] = useState(false);
    // 重新检测后仍未发现 svn 时的提示；为空表示没提示（首次进入或检测成功）
    const [refreshHostCapsHint, setRefreshHostCapsHint] = useState('');
    useEffect(() => {
        if (!visible)
            return;
        resetState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);
    function resetState() {
        setPhase('type-select');
        setRepoType(null);
        setRepos([]);
        setKeyword('');
        setSelected({});
        setBranchByRepo({});
        setBranchOptionsByRepo({});
        setCredentialUid('');
        setSshPublicKey('');
        setShowPrivateTokenInput(false);
        setPrivateTokenValue('');
        setLoadReposErrorMessage('');
        setSvnUrl('');
        setSvnUsername('');
        setSvnPassword('');
        setSvnTestErrorText('');
        setSvnCandidatePaths([]);
        setSvnSelectedPaths({});
        setRefreshingHostCaps(false);
        setRefreshHostCapsHint('');
    }
    // ======================== 类型选择（2026-08-13 新增，见 spec §7.1） ========================
    /**
     * 2026-09-08 新增（TAPD 1020421727137935765 code review 发现的 Critical bug 修复）：
     * GIT 和 TSVN 共用同一套 `selected`/`branchByRepo`/`branchOptionsByRepo`/`repos`/
     * `credentialUid`/`sshPublicKey` state（`loading`/`picking`/`key` 这套 phase 机器本身
     * 就是两者共用的）。此前只有 GIT 会写这些 state（SVN 私有化走完全独立的
     * `svnSelectedPaths` 一套状态），"点「上一步」回类型选择不清空 selected"这个老 gap
     * 从未被触达；新增 TSVN 后，「Git 选了仓库 → 上一步 → 切到 TSVN」会让上一次选中的
     * Git repoId 原样留在 `selected` 里，`selectedRepoIds` 不会因为 repos 列表换成 TSVN
     * 就跟着清空，最终 `handleSave` 会把这个查不到的旧 repoId 当 TSVN 仓库绑定提交给
     * 后端——静默产生错误的 repo binding，用户全程无感知。必须在两者之间切换时清空。
     */
    function resetGitLikePickingState() {
        setRepos([]);
        setKeyword('');
        setSelected({});
        setBranchByRepo({});
        setBranchOptionsByRepo({});
        setCredentialUid('');
        setSshPublicKey('');
        setLoadReposErrorMessage('');
    }
    function handlePickGit() {
        resetGitLikePickingState();
        setRepoType('git');
        setPhase('loading');
        void loadRepos('', 'GIT');
    }
    /**
     * 2026-09-08 新增（TAPD 1020421727137935765）：工蜂 TSVN（工蜂原生 SVN 项目，SSH 公钥
     * 模式）。跟 handlePickGit 完全对称——两者共用 loading/need-oauth/error/picking/key
     * 这一整套 phase 机器，区别只在于 `repoType` 状态驱动内部各函数按 GIT 还是 SVN
     * 调用对应接口（listGiteeRepos 的 type 参数、listGitBranches vs listTsvnPaths、
     * createGitCredential vs createTsvnCredential），不新增任何 phase。
     */
    function handlePickTsvn() {
        resetGitLikePickingState();
        setRepoType('tsvn');
        setPhase('loading');
        void loadRepos('', 'SVN');
    }
    function handlePickSvn() {
        if (svnCapabilityMissing) {
            // 2026-08-14：缺程序能力时卡片本身已灰置（见 renderTypeSelect），这里是一层
            // 兜底防御，真正的强制校验仍在「校验连接」的 BRIDGE_OUTDATED 分支。
            message.warning(ze(SVN_ERROR_REASON_TEXT.BRIDGE_OUTDATED.zh, SVN_ERROR_REASON_TEXT.BRIDGE_OUTDATED.en));
            return;
        }
        setRepoType('svn');
        // 2026-08-14：宿主机明确没装 svn（hostCapabilities 已上报且不含 svn_cli）时，
        // 卡片不再灰置，改为进入「环境未就绪」引导步骤，展示可复制的安装命令 +
        // 「重新检测」按钮；hostCapabilities 未知（老 Bridge 没上报过）或已装好时
        // 直接进表单——前者由 svnCapabilityMissing 兜底（老 Bridge 不会走到这里）。
        if (svnCliKnownMissing) {
            setRefreshHostCapsHint('');
            setPhase('svn-env-not-ready');
        }
        else {
            // 追加路径模式：预填已存凭证的 svnUrl/username（只读），不填密码
            if (existingSvnCredential) {
                setSvnUrl(existingSvnCredential.svnUrl || '');
                setSvnUsername(existingSvnCredential.username || '');
                setSvnPassword('');
            }
            setPhase('svn-form');
        }
    }
    function handleBackToTypeSelect() {
        setRepoType(null);
        setPhase('type-select');
    }
    // ======================== Git 流程（P0，逻辑不变） ========================
    /**
     * 2026-09-08 新增 `type` 参数（TAPD 1020421727137935765）：'GIT'（工蜂 Git 卡片）或
     * 'SVN'（工蜂 TSVN 卡片）。两者共用同一个工蜂账号级 OAuth 授权状态（`authorized`），
     * 也共用 need-oauth/error/picking 这几个 phase，唯一区别是传给后端的 type 不同。
     */
    async function loadRepos(kw, type, opts) {
        if (!opts?.silent)
            setSearching(true);
        try {
            // 2026-08-04 晚更新（见 spec §2.3.6）：listGiteeRepos 改为经该工作区的 Bridge
            // 转发工蜂 API 请求（api-server 直连不了工蜂所在的 devcloud 网络），因此需要
            // 带上 workspaceUid，后端才知道转发给哪个 Bridge。
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/listGiteeRepos`, { workspaceUid, keyword: kw, type });
            const data = rsp.json.data;
            if (!data || !data.authorized) {
                setPhase('need-oauth');
                setRepos([]);
                return;
            }
            setRepos(data.repos || []);
            setPhase('picking');
        }
        catch (e) {
            // 2026-08-09 review 修复：这里原来无论什么错误都 setPhase('need-oauth')，
            // 会把"已经授权过、只是这次查询临时失败"（Bridge 离线/网络抖动/工蜂 5xx）
            // 误导成"还没授权"，用户明明配置过还是会被引导去重新走一遍连接工蜂账号的流程。
            // 改成单独的 error phase，展示失败原因 + 重试按钮，不跟"未授权"混在一起。
            console.error('listGiteeRepos failed', e);
            const errMsg = describeGongfengApiError(e, ze('查询工蜂仓库列表失败，请稍后重试', 'Failed to list Gitee repositories, please retry later'));
            message.error(errMsg);
            setLoadReposErrorMessage(errMsg);
            setPhase('error');
        }
        finally {
            setSearching(false);
            setCheckingOAuth(false);
        }
    }
    async function handleRecheckOAuth() {
        setCheckingOAuth(true);
        await loadRepos('', repoType === 'tsvn' ? 'SVN' : 'GIT', { silent: true });
    }
    /**
     * 发起工蜂原生 OAuth2（PKCE）授权（见 spec §2.3.7）：拿到授权 URL 后开一个新窗口
     * 跳转到工蜂授权页，用户完成授权后工蜂会重定向到后端的回调接口换 token；本窗口
     * 不会自动感知回调结果，需要用户手动点击「我已完成，重新检测」（沿用原有按钮）。
     */
    async function handleConnectGongfeng() {
        if (!bridgeOnline) {
            message.warning(ze('请先确保当前工作区 Bridge 在线后重试', "Please make sure this workspace's Bridge is online, then retry"));
            return;
        }
        setConnectingOAuth(true);
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/gongfengOauthAuthorize`, { workspaceUid });
            const redirectUrl = rsp.json.data?.redirectUrl;
            if (!redirectUrl) {
                throw new Error('redirectUrl is empty');
            }
            window.open(redirectUrl, '_blank', 'width=640,height=760,noopener,noreferrer');
        }
        catch (e) {
            console.error('gongfengOauthAuthorize failed', e);
            message.error(describeGongfengApiError(e, ze('发起工蜂授权失败，请稍后重试', 'Failed to start Gitee OAuth authorization, please retry later')));
        }
        finally {
            setConnectingOAuth(false);
        }
    }
    /** 次要兜底：手动配置工蜂 Private Token / Personal Access Token（见 spec §2.3.7）。 */
    async function handleSavePrivateToken() {
        const trimmed = privateTokenValue.trim();
        if (!trimmed) {
            message.warning(ze('请输入 Private Token', 'Please enter a Private Token'));
            return;
        }
        setSavingPrivateToken(true);
        try {
            await RestHelper.mustPost(`${API_PREFIX}/configureGiteePrivateToken`, { privateToken: trimmed });
            message.success(ze('Private Token 已保存', 'Private Token saved'));
            setShowPrivateTokenInput(false);
            setPrivateTokenValue('');
            await handleRecheckOAuth();
        }
        catch (e) {
            console.error('configureGiteePrivateToken failed', e);
            message.error(ze('保存失败，请稍后重试', 'Save failed, please try again'));
        }
        finally {
            setSavingPrivateToken(false);
        }
    }
    function toggleRepo(repoId, checked) {
        setSelected(prev => ({ ...prev, [repoId]: checked }));
        if (checked && !branchOptionsByRepo[repoId]) {
            void loadRefOptions(repoId);
        }
    }
    /**
     * 2026-09-08 更新（TAPD 1020421727137935765）：原 `loadBranches` 改名为 `loadRefOptions`
     * 并按 `repoType` 分流——GIT 查分支列表（`listGitBranches`），TSVN 查目录树
     * （`listTsvnPaths`，SVN 没有分支概念，用 trunk/branches/tags 一层子目录代替，见
     * 工蜂官方文档 `docs/menu/api/svn/svn_repositorys.md`）。两者统一映射成
     * `GitBranchItem[]`（`commitId` 对 TSVN 恒为空，反正下拉框 UI 本来就只展示 `name`）
     * 复用同一个 `branchOptionsByRepo` state 和 `renderPicking` 渲染逻辑，不用另开一套。
     */
    async function loadRefOptions(repoId) {
        setBranchLoadingRepo(repoId);
        try {
            if (repoType === 'tsvn') {
                const rsp = await RestHelper.mustPost(`${API_PREFIX}/listTsvnPaths`, {
                    workspaceUid,
                    repoId,
                    path: '/',
                });
                const paths = rsp.json.data || [];
                setBranchOptionsByRepo(prev => ({ ...prev, [repoId]: paths.map(p => ({ name: p })) }));
            }
            else {
                const rsp = await RestHelper.mustPost(`${API_PREFIX}/listGitBranches`, {
                    workspaceUid,
                    repoId,
                });
                setBranchOptionsByRepo(prev => ({ ...prev, [repoId]: rsp.json.data || [] }));
            }
        }
        catch (e) {
            console.error('loadRefOptions failed', e);
            message.error(describeGongfengApiError(e, repoType === 'tsvn'
                ? ze('查询目录列表失败', 'Failed to list directories')
                : ze('查询分支列表失败', 'Failed to list branches')));
        }
        finally {
            setBranchLoadingRepo(null);
        }
    }
    const selectedRepoIds = useMemo(() => Object.keys(selected).filter(id => selected[id]), [selected]);
    async function handleNextStep() {
        if (selectedRepoIds.length === 0) {
            message.warning(ze('请至少选择一个仓库', 'Please select at least one repository'));
            return;
        }
        setCreatingCredential(true);
        try {
            // 2026-09-08 新增：repoType=tsvn 走 createTsvnCredential（独立生成密钥对，不复用
            // Git 认证组的公钥，见 design.md 风险点 2），其余结构与 Git 完全一致。
            const endpoint = repoType === 'tsvn' ? 'createTsvnCredential' : 'createGitCredential';
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/${endpoint}`, {
                workspaceUid,
            });
            const data = rsp.json.data;
            setCredentialUid(data.credentialUid);
            setSshPublicKey(data.sshPublicKey);
            setPhase('key');
        }
        catch (e) {
            console.error('createGitCredential/createTsvnCredential failed', e);
            message.error(ze('生成 SSH 密钥对失败，请确认 Bridge 已连接后重试', 'Failed to generate SSH keypair, please ensure Bridge is connected and retry'));
        }
        finally {
            setCreatingCredential(false);
        }
    }
    async function handleSave() {
        setSaving(true);
        try {
            const bindings = selectedRepoIds.map(repoId => {
                const repo = repos.find(r => r.repoId === repoId);
                const repoRef = branchByRepo[repoId] || repo?.defaultBranch || undefined;
                return {
                    repoIdentifier: repoId,
                    repoRef,
                    displayName: repo?.name || repoId,
                };
            });
            await RestHelper.mustPost(`${API_PREFIX}/createRepoBindings`, {
                credentialUid,
                bindings,
            });
            message.success(ze('仓库授权已保存', 'Repository authorization saved'));
            onSaved();
            onClose();
        }
        catch (e) {
            console.error('createRepoBindings failed', e);
            message.error(ze('保存失败，请稍后重试', 'Save failed, please try again'));
        }
        finally {
            setSaving(false);
        }
    }
    function copyPublicKey() {
        if (!sshPublicKey)
            return;
        navigator.clipboard.writeText(sshPublicKey);
        message.success(ze('公钥已复制', 'Public key copied'));
    }
    // ======================== SVN 流程（2026-08-13 新增 P1，见 spec §2.7/§5.2.4/§5.2.6） ========================
    async function handleTestSvnConnection() {
        // 2026-08-14：追加路径模式不要求填 url/username/password（复用已存凭证，密码服务端解密）
        if (!svnAddPathsMode) {
            if (!svnUrl.trim() || !svnUsername.trim() || !svnPassword) {
                message.warning(ze('请填写完整的仓库 URL / 用户名 / 密码', 'Please fill in URL / username / password'));
                return;
            }
        }
        if (!bridgeOnline) {
            message.warning(ze('请先确保当前工作区 Bridge 在线后重试', "Please make sure this workspace's Bridge is online, then retry"));
            return;
        }
        setTestingSvn(true);
        setSvnTestErrorText('');
        try {
            // 追加路径模式：只传 credentialUid，后端用已存凭证做校验（密码不出服务器）
            const body = svnAddPathsMode
                ? { workspaceUid, credentialUid: existingSvnCredential.credentialUid }
                : { workspaceUid, svnUrl: svnUrl.trim(), username: svnUsername.trim(), password: svnPassword };
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/testSvnConnection`, body);
            const data = rsp.json.data;
            if (!data?.success) {
                setSvnTestErrorText(describeSvnErrorReason(data?.errorReason, data?.message));
                return;
            }
            setSvnCandidatePaths(data.paths || []);
            setSvnSelectedPaths({});
            message.success(ze('连接成功', 'Connected successfully'));
            setPhase('svn-picking');
        }
        catch (e) {
            console.error('testSvnConnection failed', e);
            setSvnTestErrorText(ze('连接校验失败，请稍后重试', 'Connection check failed, please retry later'));
        }
        finally {
            setTestingSvn(false);
        }
    }
    const selectedSvnPathList = useMemo(() => Object.keys(svnSelectedPaths).filter(p => svnSelectedPaths[p]), [svnSelectedPaths]);
    function toggleSvnPath(path, checked) {
        setSvnSelectedPaths(prev => ({ ...prev, [path]: checked }));
    }
    /** 从完整路径 URL 中截取相对于 svnUrl 的子路径（作为 repoRef），供保存时使用。 */
    function relativeSvnRef(fullPath) {
        const base = svnUrl.trim().replace(/\/+$/, '');
        if (fullPath === base)
            return undefined;
        if (fullPath.startsWith(`${base}/`)) {
            return fullPath.slice(base.length);
        }
        return fullPath;
    }
    /**
     * 2026-08-14 新增：取 svnUrl 去掉 `scheme://host` 后的 path 部分，作为 displayName。
     * SVN 没有工蜂那样的仓库名 API，任何"取某一段当仓库名"的启发式都有坑（公共前缀路径、
     * 用户填到不同层级等），所以直接用 path 部分当名称——绝对不错、可识别，配合 repoRef
     * （选中的子路径）展示，跟 Git 的"仓库名 + 分支"模式一致。例如
     * `svn://25.1.39.145/crashclaw-test` → `/crashclaw-test`，选中 trunk 时显示
     * `/crashclaw-test  /trunk`。
     */
    function svnUrlPath(url) {
        const m = url.match(/^[a-z]+:\/\/[^/?#]+(.*)$/i);
        return m && m[1] ? m[1] : url;
    }
    async function handleSaveSvn() {
        setSavingSvn(true);
        try {
            // 2026-08-14：追加路径模式复用已存 credentialUid，不调 createSvnCredential
            // （单组限制下 createSvnCredential 会撞"单一 SVN 账号"报错）
            let newCredentialUid;
            if (svnAddPathsMode) {
                newCredentialUid = existingSvnCredential.credentialUid;
            }
            else {
                const createRsp = await RestHelper.mustPost(`${API_PREFIX}/createSvnCredential`, {
                    workspaceUid,
                    svnUrl: svnUrl.trim(),
                    username: svnUsername.trim(),
                    password: svnPassword,
                });
                newCredentialUid = createRsp.json.data?.credentialUid;
                if (!newCredentialUid) {
                    throw new Error('credentialUid is empty');
                }
            }
            // 2026-08-14：displayName 用 svnUrl 的 path 部分（去掉 scheme://host），不用启发式
            // 推导仓库名（任何"取某一段"的启发式都有坑，见 svnUrlPath 注释）；选中的子路径已经
            // 记录在 repoRef 里，displayName 只代表"仓库"本身，跟 Git 的"仓库名+分支"一致。
            const effectiveDisplayName = svnUrlPath(svnUrl.trim());
            // 未选任何子路径时，默认只授权根路径这一条绑定（见 spec §7.1 SVN 流程）
            const bindings = selectedSvnPathList.length > 0
                ? selectedSvnPathList.map(fullPath => ({
                    repoIdentifier: svnUrl.trim(),
                    repoRef: relativeSvnRef(fullPath),
                    displayName: effectiveDisplayName,
                }))
                : [{
                        repoIdentifier: svnUrl.trim(),
                        repoRef: undefined,
                        displayName: effectiveDisplayName,
                    }];
            await RestHelper.mustPost(`${API_PREFIX}/createRepoBindings`, {
                credentialUid: newCredentialUid,
                bindings,
            });
            message.success(ze('SVN 仓库授权已保存', 'SVN repository authorization saved'));
            onSaved();
            onClose();
        }
        catch (e) {
            console.error('createSvnCredential/createRepoBindings failed', e);
            const backendMessage = e?.message;
            message.error(backendMessage || ze('保存失败，请稍后重试', 'Save failed, please try again'));
        }
        finally {
            setSavingSvn(false);
        }
    }
    // ======================== SVN 环境检测（2026-08-14 新增，见 spec 2026-08-14 记录） ========================
    /**
     * 「重新检测」按钮：主动让 Bridge 重跑 detect_host_capabilities() 并落库，
     * 拿到最新 hostCapabilities。装完 svn 后点一下即时反馈，不需要重启/升级 Bridge。
     */
    async function handleRefreshHostCaps() {
        if (!bridgeOnline) {
            message.warning(ze('请先确保当前工作区 Bridge 在线后重试', "Please make sure this workspace's Bridge is online, then retry"));
            return;
        }
        setRefreshingHostCaps(true);
        setRefreshHostCapsHint('');
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/refreshHostCapabilities`, {
                workspaceUid,
            });
            const data = rsp.json.data;
            if (data?.svnCliInstalled) {
                message.success(ze('已检测到 svn 命令行客户端，可以填写连接信息了', 'svn CLI detected, you can fill in the connection info now'));
                // 通知父组件刷新工作区列表，使 svnCliKnownMissing prop 跟 DB 最新值对齐
                onHostCapabilitiesRefreshed?.();
                setPhase('svn-form');
            }
            else {
                setRefreshHostCapsHint(ze('仍未检测到 svn 命令行客户端，请确认安装命令执行成功后再次点击「重新检测」', 'svn CLI still not detected. Please confirm the install command succeeded, then click "Re-detect" again.'));
                // 即便没装上，也通知父组件刷新一次（hostCapabilities 数组可能已落库更新）
                onHostCapabilitiesRefreshed?.();
            }
        }
        catch (e) {
            console.error('refreshHostCapabilities failed', e);
            const backendMessage = e?.message;
            setRefreshHostCapsHint(backendMessage || ze('重新检测失败，请稍后重试', 'Re-detect failed, please retry later'));
        }
        finally {
            setRefreshingHostCaps(false);
        }
    }
    function copyInstallCommand(cmd) {
        navigator.clipboard.writeText(cmd);
        message.success(ze('已复制', 'Copied'));
    }
    // ======================== 渲染 ========================
    function renderTypeSelect() {
        // 2026-08-14 更新：SVN 卡片只在缺 Bridge **程序能力**（必须升级 Bridge）时灰置；
        // 缺宿主机 svn CLI（装软件即可）时卡片**可点击**，点击进入「环境未就绪」引导步骤
        // （展示可复制安装命令 + 「重新检测」按钮），不再用 tooltip 承载难以复制的安装命令。
        const svnCardDisabled = svnCapabilityMissing;
        const svnCard = (_jsxs("div", { className: `${style.repoTypeCard} ${svnCardDisabled ? style.repoTypeCardDisabled : ''}`, onClick: handlePickSvn, children: [_jsx(FolderOutlined, { className: style.repoTypeCardIcon }), _jsx("div", { className: style.repoTypeCardTitle, children: ze('SVN', 'SVN') }), _jsx("div", { className: style.repoTypeCardDesc, children: ze('用户名 + 密码模式', 'Username + password') })] }));
        // tooltip 只在"缺程序能力"（灰置）时展示，文案指向升级 Bridge——这是一次性导航动作，
        // 不需要复制命令；缺 svn CLI 的引导改由「环境未就绪」步骤承载，不再走 tooltip。
        const svnTooltipTitle = svnCardDisabled
            ? ze(SVN_ERROR_REASON_TEXT.BRIDGE_OUTDATED.zh, SVN_ERROR_REASON_TEXT.BRIDGE_OUTDATED.en)
            : null;
        return (_jsxs("div", { className: style.repoTypeCardList, children: [_jsxs("div", { className: style.repoTypeCard, onClick: handlePickGit, children: [_jsx(GithubOutlined, { className: style.repoTypeCardIcon }), _jsx("div", { className: style.repoTypeCardTitle, children: ze('工蜂 Git', 'Gitee (Git)') }), _jsx("div", { className: style.repoTypeCardDesc, children: ze('SSH 公钥模式', 'SSH public key') })] }), svnTooltipTitle ? (_jsx(Tooltip, { title: svnTooltipTitle, children: svnCard })) : svnCard, _jsxs("div", { className: style.repoTypeCard, onClick: handlePickTsvn, children: [_jsx(CloudServerOutlined, { className: style.repoTypeCardIcon }), _jsx("div", { className: style.repoTypeCardTitle, children: ze('工蜂 TSVN', 'Gitee TSVN') }), _jsx("div", { className: style.repoTypeCardDesc, children: ze('SSH 公钥模式', 'SSH public key') })] })] }));
    }
    function renderNeedOAuth() {
        return (_jsxs("div", { className: style.repoAuthorizeGuide, children: [_jsx("div", { className: style.repoAuthorizeGuideText, children: ze('尚未完成工蜂授权。点击下方按钮连接你的工蜂账号，授权完成后回来选择仓库。', 'Gitee authorization not completed yet. Click below to connect your Gitee account, then come back to pick repositories.') }), !bridgeOnline && (_jsx("div", { className: style.repoAuthorizeGuideHint, children: ze('当前工作区 Bridge 离线：换取工蜂 token 需要经该工作区的 Bridge 转发，请先确保 Bridge 在线后再发起授权。', "This workspace's Bridge is offline: exchanging the Gitee token needs to be relayed through this workspace's Bridge. Please bring it online before authorizing.") })), _jsxs("div", { className: style.repoAuthorizeGuideActions, children: [_jsx(Button, { type: "primary", disabled: !bridgeOnline, loading: connectingOAuth, onClick: handleConnectGongfeng, children: ze('连接工蜂账号', 'Connect Gitee Account') }), _jsx(Button, { icon: _jsx(ReloadOutlined, {}), loading: checkingOAuth, onClick: handleRecheckOAuth, children: ze('我已完成，重新检测', "I've done it, re-check") })] }), _jsx("div", { className: style.repoAuthorizeGuideFallback, children: !showPrivateTokenInput ? (_jsx("a", { onClick: () => setShowPrivateTokenInput(true), children: ze('或者，手动配置工蜂 Private Token', 'Or, manually configure a Gitee Private Token') })) : (_jsxs("div", { className: style.repoAuthorizeGuideFallbackForm, children: [_jsx(CsFakePasswordInput, { placeholder: ze('粘贴工蜂 Private Token / Personal Access Token', 'Paste your Gitee Private Token / Personal Access Token'), value: privateTokenValue, onChange: (e) => setPrivateTokenValue(e.target.value), style: { width: 320, marginRight: 8 } }), _jsx(Button, { size: "small", type: "primary", loading: savingPrivateToken, onClick: handleSavePrivateToken, children: ze('保存', 'Save') })] })) })] }));
    }
    function renderError() {
        return (_jsxs("div", { className: style.repoAuthorizeGuide, children: [_jsx("div", { className: style.repoAuthorizeGuideText, children: loadReposErrorMessage || ze('查询工蜂仓库列表失败，请稍后重试', 'Failed to list Gitee repositories, please retry later') }), _jsx("div", { className: style.repoAuthorizeGuideActions, children: _jsx(Button, { icon: _jsx(ReloadOutlined, {}), loading: searching, onClick: () => void loadRepos(keyword, repoType === 'tsvn' ? 'SVN' : 'GIT'), children: ze('重试', 'Retry') }) })] }));
    }
    function renderPicking() {
        return (_jsxs("div", { className: style.repoAuthorizeBody, children: [_jsx(Input, { prefix: _jsx(SearchOutlined, {}), placeholder: ze('搜索仓库名称', 'Search by repository name'), value: keyword, allowClear: true, onChange: (e) => setKeyword(e.target.value), onPressEnter: () => { void loadRepos(keyword, repoType === 'tsvn' ? 'SVN' : 'GIT'); }, style: { marginBottom: 12 } }), _jsx("div", { className: style.repoAuthorizeList, children: searching ? (_jsx("div", { className: style.repoAuthorizeSpin, children: _jsx(Spin, {}) })) : repos.length === 0 ? (_jsx(Empty, { description: ze('未找到匹配仓库', 'No matching repository') })) : (repos.map(repo => {
                        const checked = !!selected[repo.repoId];
                        const alreadyBound = !!boundRepoIdentifiers?.includes(repo.repoId);
                        const branchOptions = branchOptionsByRepo[repo.repoId] || [];
                        return (_jsxs("div", { className: style.repoAuthorizeRow, children: [_jsxs(Checkbox, { checked: checked, disabled: alreadyBound, onChange: (e) => toggleRepo(repo.repoId, e.target.checked), children: [_jsx("span", { className: style.repoAuthorizeRowName, children: repo.name }), _jsx("span", { className: style.repoAuthorizeRowId, children: repo.repoId }), alreadyBound && (_jsx("span", { className: style.repoAuthorizeRowId, children: ze('（已绑定）', '(already bound)') }))] }), checked && (_jsx(Select, { size: "small", style: { width: 180 }, placeholder: repo.defaultBranch || (repoType === 'tsvn'
                                        ? ze('选择子路径（不选则用根路径）', 'Pick a sub-path (root if empty)')
                                        : ze('选择分支', 'Select branch')), value: branchByRepo[repo.repoId] || repo.defaultBranch || undefined, loading: branchLoadingRepo === repo.repoId, showSearch: true, onChange: (v) => setBranchByRepo(prev => ({ ...prev, [repo.repoId]: v })), options: (branchOptions.length > 0
                                        ? branchOptions
                                        : repo.defaultBranch ? [{ name: repo.defaultBranch }] : []).map(b => ({ value: b.name, label: b.name })), notFoundContent: branchLoadingRepo === repo.repoId ? _jsx(Spin, { size: "small" }) : null }))] }, repo.repoId));
                    })) })] }));
    }
    function renderKeyStep() {
        return (_jsxs("div", { className: style.repoAuthorizeBody, children: [_jsx("div", { className: style.repoAuthorizeGuideText, children: ze('请复制下方公钥，登录工蜂账号 → 个人设置 → SSH Key，添加此公钥后再点击「保存」。', 'Copy the public key below, log into Gitee → Profile Settings → SSH Keys, add it, then click "Save".') }), _jsxs("div", { className: style.repoAuthorizeKeyBox, children: [_jsx("code", { children: sshPublicKey }), _jsx(Button, { size: "small", icon: _jsx(CopyOutlined, {}), onClick: copyPublicKey, children: ze('复制', 'Copy') })] }), _jsx("div", { className: style.repoAuthorizeSelectedSummary, children: ze(`已选择 ${selectedRepoIds.length} 个仓库`, `${selectedRepoIds.length} repositories selected`) })] }));
    }
    function renderSvnForm() {
        return (_jsxs("div", { className: style.repoAuthorizeBody, children: [_jsx("div", { className: style.repoAuthorizeGuideText, children: svnAddPathsMode
                        ? ze('该工作区已有 SVN 凭证，校验连接后选择要追加授权的子路径（复用已存凭证，无需重填密码）。', 'An SVN credential already exists for this workspace. Test the connection, then pick additional sub-paths to authorize (reusing the existing credential, no password re-entry).')
                        : ze('填写用户私有化部署的 SVN 服务器信息，校验连接通过后即可选择要授权的路径。', 'Fill in your self-hosted SVN server info. Once connected, pick the paths to authorize.') }), _jsxs("div", { className: style.svnFormFieldGroup, children: [_jsx(Input, { placeholder: ze('仓库 URL，如 svn://svn.company.com/repo', 'Repository URL, e.g. svn://svn.company.com/repo'), value: svnUrl, onChange: (e) => setSvnUrl(e.target.value), disabled: svnAddPathsMode }), _jsx(Input, { placeholder: ze('用户名', 'Username'), value: svnUsername, onChange: (e) => setSvnUsername(e.target.value), autoComplete: "off", disabled: svnAddPathsMode }), !svnAddPathsMode && (_jsx(CsFakePasswordInput, { placeholder: ze('密码', 'Password'), value: svnPassword, onChange: (e) => setSvnPassword(e.target.value) }))] }), svnTestErrorText && (_jsx("div", { className: style.repoAuthorizeGuideHint, children: svnTestErrorText })), !bridgeOnline && (_jsx("div", { className: style.repoAuthorizeGuideHint, children: ze('当前工作区 Bridge 离线：连接校验需要经该工作区的 Bridge 转发，请先确保 Bridge 在线。', "This workspace's Bridge is offline: the connection check needs to be relayed through this workspace's Bridge.") }))] }));
    }
    function renderSvnEnvNotReady() {
        // 2026-08-14 新增：Bridge 程序能力 OK 但宿主机没装 svn 时进入这个引导步骤。
        // 安装命令用可复制的代码块展示（替代之前难以复制的 tooltip），「重新检测」按钮
        // 主动触发 Bridge 重跑 detect_host_capabilities()，装完 svn 点一下即时反馈。
        const aptCmd = 'apt-get install -y subversion';
        const yumCmd = 'yum install -y subversion';
        return (_jsxs("div", { className: style.repoAuthorizeBody, children: [_jsx("div", { className: style.repoAuthorizeGuideText, children: ze('该工作区 Bridge 所在容器尚未安装 svn 命令行客户端。请登录容器执行下列任一命令安装，完成后点击「重新检测」。', "The svn CLI is not installed on this workspace's Bridge host. Please run one of the commands below inside the container, then click \"Re-detect\".") }), _jsxs("div", { className: style.svnInstallCmdGroup, children: [_jsxs("div", { className: style.svnInstallCmdRow, children: [_jsx("code", { children: aptCmd }), _jsx(Button, { size: "small", icon: _jsx(CopyOutlined, {}), onClick: () => copyInstallCommand(aptCmd), children: ze('复制', 'Copy') })] }), _jsxs("div", { className: style.svnInstallCmdRow, children: [_jsx("code", { children: yumCmd }), _jsx(Button, { size: "small", icon: _jsx(CopyOutlined, {}), onClick: () => copyInstallCommand(yumCmd), children: ze('复制', 'Copy') })] })] }), refreshHostCapsHint && (_jsx("div", { className: style.repoAuthorizeGuideHint, children: refreshHostCapsHint })), !bridgeOnline && (_jsx("div", { className: style.repoAuthorizeGuideHint, children: ze('当前工作区 Bridge 离线：重新检测需要经该工作区的 Bridge 转发，请先确保 Bridge 在线。', "This workspace's Bridge is offline: re-detection needs to be relayed through this workspace's Bridge.") }))] }));
    }
    function renderSvnPicking() {
        return (_jsxs("div", { className: style.repoAuthorizeBody, children: [_jsx("div", { className: style.repoAuthorizeGuideText, children: ze('连接成功，可从下方选择要授权的子路径（不选则只授权根路径）。', 'Connected successfully. Optionally pick sub-paths below (leave empty to authorize the root path only).') }), _jsx("div", { className: style.repoAuthorizeList, children: svnCandidatePaths.length === 0 ? (_jsx(Empty, { description: ze('未检测到子路径，将只授权根路径', 'No sub-paths detected, only the root path will be authorized') })) : (svnCandidatePaths.map(path => (_jsx("div", { className: style.repoAuthorizeRow, children: _jsx(Checkbox, { checked: !!svnSelectedPaths[path], onChange: (e) => toggleSvnPath(path, e.target.checked), children: _jsx("span", { className: style.repoAuthorizeRowId, children: path }) }) }, path)))) })] }));
    }
    const stepIndex = phase === 'key' || phase === 'svn-picking' ? 1 : 0;
    const footer = (() => {
        if (phase === 'type-select') {
            return _jsx(Button, { onClick: onClose, children: ze('取消', 'Cancel') });
        }
        if (phase === 'loading') {
            return _jsx(Button, { onClick: onClose, children: ze('取消', 'Cancel') });
        }
        if (phase === 'need-oauth' || phase === 'error') {
            return (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: handleBackToTypeSelect, children: ze('上一步', 'Back') }), _jsx(Button, { onClick: onClose, children: ze('关闭', 'Close') })] }));
        }
        if (phase === 'key') {
            return (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: () => setPhase('picking'), children: ze('上一步', 'Back') }), _jsx(Button, { type: "primary", loading: saving, onClick: handleSave, children: ze('保存', 'Save') })] }));
        }
        if (phase === 'picking') {
            return (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: handleBackToTypeSelect, children: ze('上一步', 'Back') }), _jsx(Button, { type: "primary", loading: creatingCredential, onClick: handleNextStep, children: ze('下一步', 'Next') })] }));
        }
        if (phase === 'svn-env-not-ready') {
            return (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: handleBackToTypeSelect, children: ze('上一步', 'Back') }), _jsx(Button, { type: "primary", icon: _jsx(ReloadOutlined, {}), loading: refreshingHostCaps, disabled: !bridgeOnline, onClick: handleRefreshHostCaps, children: ze('重新检测', 'Re-detect') })] }));
        }
        if (phase === 'svn-form') {
            return (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: handleBackToTypeSelect, children: ze('上一步', 'Back') }), _jsx(Button, { type: "primary", loading: testingSvn, onClick: handleTestSvnConnection, children: ze('校验连接', 'Test Connection') })] }));
        }
        if (phase === 'svn-picking') {
            return (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: () => setPhase('svn-form'), children: ze('上一步', 'Back') }), _jsx(Button, { type: "primary", loading: savingSvn, onClick: handleSaveSvn, children: ze('保存', 'Save') })] }));
        }
        return null;
    })();
    return (_jsxs(Modal, { title: _jsxs("span", { children: [repoType === 'svn' ? _jsx(FolderOutlined, { style: { marginRight: 8 } })
                    : repoType === 'git' ? _jsx(GithubOutlined, { style: { marginRight: 8 } })
                        : repoType === 'tsvn' ? _jsx(CloudServerOutlined, { style: { marginRight: 8 } })
                            : _jsx(DatabaseOutlined, { style: { marginRight: 8 } }), ze('授权代码仓库', 'Authorize Repository')] }), open: visible, onCancel: onClose, width: 620, maskClosable: false, 
        // 显式声明 zIndex，保证始终盖在"工作区详情"弹窗（CrashClawWorkspacePanel.tsx）之上
        zIndex: CRASHCLAW_NESTED_MODAL_Z_INDEX, footer: footer, children: [(phase === 'picking' || phase === 'key') && (_jsxs(Steps, { size: "small", current: stepIndex, style: { marginBottom: 16 }, children: [_jsx(Steps.Step, { title: ze('选择仓库', 'Pick repos') }), _jsx(Steps.Step, { title: ze('添加公钥', 'Add public key') })] })), (phase === 'svn-form' || phase === 'svn-picking') && (_jsxs(Steps, { size: "small", current: stepIndex, style: { marginBottom: 16 }, children: [_jsx(Steps.Step, { title: ze('填写连接信息', 'Connection info') }), _jsx(Steps.Step, { title: ze('选择路径', 'Pick paths') })] })), phase === 'type-select' && renderTypeSelect(), phase === 'loading' && (_jsx("div", { className: style.repoAuthorizeSpin, children: _jsx(Spin, {}) })), phase === 'need-oauth' && renderNeedOAuth(), phase === 'error' && renderError(), phase === 'picking' && renderPicking(), phase === 'key' && renderKeyStep(), phase === 'svn-form' && renderSvnForm(), phase === 'svn-env-not-ready' && renderSvnEnvNotReady(), phase === 'svn-picking' && renderSvnPicking()] }));
};
export default RepoAuthorizeModal;
